/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment, useState } from 'react'

import { EuiFilterGroup, EuiFlexGroup, EuiFlexItem, EuiSpacer } from '@elastic/eui'

import IncludeDeletedFilter from './IncludeDeletedFilter'
import OrganizationFilter from './OrganizationFilter'
import ProjectIdFilter from './ProjectIdFilter'
import SolutionFilter from './ProjectTypeFilter'
import RegionFilter from './RegionFilter'

export type Filter = 'organization' | 'projectId' | 'type' | 'region'

type HideableFilter = Extract<Filter, 'organization'>
type PopoverId = Extract<Filter, 'type' | 'region'>

type Props = {
  hide?: HideableFilter[]
}

const AdminProjectListFilters: React.FunctionComponent<Props> = ({ hide }) => {
  const [openPopover, toggleOpenPopover] = useMultiToggle<PopoverId>(null)

  return (
    <Fragment>
      <EuiFlexGroup justifyContent='spaceBetween'>
        <EuiFlexItem grow={false}>
          <EuiFlexGroup gutterSize='m' justifyContent='flexStart'>
            {hide?.includes('organization') || (
              <EuiFlexItem grow={false}>
                <OrganizationFilter />
              </EuiFlexItem>
            )}
            <EuiFlexItem grow={false}>
              <ProjectIdFilter />
            </EuiFlexItem>
          </EuiFlexGroup>
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiFlexGroup justifyContent='flexEnd' alignItems='center'>
            <EuiFlexItem>
              <IncludeDeletedFilter />
            </EuiFlexItem>
            <EuiFlexItem>
              <EuiFilterGroup>
                <SolutionFilter
                  isOpen={openPopover === 'type'}
                  onToggle={() => toggleOpenPopover('type')}
                  onClose={() => toggleOpenPopover(null)}
                />
                <RegionFilter
                  isOpen={openPopover === 'region'}
                  onToggle={() => toggleOpenPopover('region')}
                  onClose={() => toggleOpenPopover(null)}
                />
              </EuiFilterGroup>
            </EuiFlexItem>
          </EuiFlexGroup>
        </EuiFlexItem>
      </EuiFlexGroup>
      <EuiSpacer size='m' />
    </Fragment>
  )
}

function useMultiToggle<T>(initialValue: T | null): [T | null, (newValue: T | null) => void] {
  const [value, setValue] = useState<T | null>(initialValue)

  return [value, (newValue: T | null) => setValue(value === newValue ? null : newValue)]
}

export default AdminProjectListFilters
